import React, { useMemo } from "react";
import { ModalBase } from "./ModalBase";
import alertTypeEnum from "../../util/alertTypeEnum";
import modalSize from "../../util/modalSizeEnum";
import { Grid2 as Grid, Typography } from "@mui/material";
import { Transfer } from "../../common/Transfer";
import { Receive } from "../../common/Receive";
import { useStyles } from "./Modal.styles";
import { getUnverifiedQuantityDifferences } from "../../util/transferUtil";
import { Alert } from "@mui/material";
import { Product } from "../../common/Product";

interface ModalUnverifiedTransferFinaliseProps {
	isOpen: boolean;
	transfer: Transfer;
	receive: Receive;
	products?: Product[];
	onCancelFinalise: () => void;
	setReceiveFinaliseModalIsOpen: (isOpen: boolean) => void;
	setPendingReceive: (isPendingReceive: boolean) => void;
}

export const ModalUnverifiedTransferFinalise: React.FC<ModalUnverifiedTransferFinaliseProps> = ({
	isOpen,
	transfer,
	receive,
	products,
	onCancelFinalise,
	setReceiveFinaliseModalIsOpen,
	setPendingReceive,
}) => {
	const classes = useStyles();

	// Calculate quantity differences once
	const quantityDifferences = useMemo(
		() => getUnverifiedQuantityDifferences(receive, transfer, products),
		[receive, transfer, products]
	);

	const onSaveAsPending = () => {
		onCancelFinalise();
		setPendingReceive(true);
		setReceiveFinaliseModalIsOpen(true);
	};

	const onFinalise = () => {
		onCancelFinalise();
		setPendingReceive(false);
		setReceiveFinaliseModalIsOpen(true);
	};

	const onGoBack = () => {
		onCancelFinalise();
	};

	return (
        <ModalBase
            title="Finalise unverified transfer?"
            alertType={alertTypeEnum.WARNING}
            primaryButtonName="Save as Pending"
            primaryOutlinedButtonName="Finalise"
            secondaryButtonName="Go Back"
            onPrimaryButtonClick={onSaveAsPending}
            onPrimaryOutlinedButtonClick={onFinalise}
            onSecondaryButtonClick={onGoBack}
            onClose={onGoBack}
            isOpen={isOpen}
            size={modalSize.FULLSCREEN}
        >
            <Grid container direction="column" spacing={3}>
                <Grid size="auto">
                    <Typography variant="body1">
                        While we cannot verify the sent quantities, you're receiving the following SKUs with different
                        quantities to what was originally requested:
                    </Typography>
                </Grid>

                <Grid size="auto" className={classes.messageList}>
                    {quantityDifferences.map((item, index) => (
                        <Typography key={index} variant="body1">
                            • <strong>{item.approvedQuantity} x SKU: {item.sku}</strong>
                            {item.description && ` ${item.description}`}
                        </Typography>
                    ))}
                </Grid>

                <Grid size="auto">
                    <Alert severity="warning">Note: If possible, check the paperwork to confirm expected quantities.</Alert>
                </Grid>

                <Grid size="auto" style={{ marginTop: 16 }}>
                    <Typography variant="body1">If you're expecting more stock, <strong>save as pending.</strong></Typography>
                    <Typography variant="body1" style={{ marginTop: 8 }}>
                        If you're not, <strong>finalise the receive</strong> now to move the scanned quantities from{" "}
                        {transfer.fromLocation.locationName} ({transfer.fromLocation.locationId}) to{" "}
                        {transfer.toLocation.locationName} ({transfer.toLocation.locationId}).
                    </Typography>
                </Grid>
            </Grid>
        </ModalBase>
	);
};
